const JSON_LD_ESCAPE_MAP: Record<string, string> = {
  '&': '\\u0026',
  '<': '\\u003c',
  '>': '\\u003e',
  '\u2028': '\\u2028',
  '\u2029': '\\u2029',
};

export function serializeJsonLd(value: unknown): string {
  const serializedValue = JSON.stringify(value);
  if (serializedValue === undefined) {
    return 'null';
  }

  return serializedValue.replace(/[&<>\u2028\u2029]/g, (character) => {
    return JSON_LD_ESCAPE_MAP[character] || character;
  });
}

/**
 * Remove keys with empty values so generated schema stays compact and valid.
 * Empty values include: undefined, null, empty string, and empty arrays.
 */
export function removeEmptyFields<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([_, item]) => {
      if (item === undefined || item === null || item === '') {
        return false;
      }

      if (Array.isArray(item)) {
        return item.length > 0;
      }

      return true;
    })
  ) as Partial<T>;
}

/**
 * Trim, deduplicate, and filter out blank keywords.
 * Returns undefined when no usable keywords remain.
 */
export function normalizeKeywords(keywords?: string[]): string[] | undefined {
  if (!keywords?.length) {
    return undefined;
  }

  const trimmedKeywords = keywords.map((keyword) => keyword.trim()).filter(Boolean);
  const normalized = Array.from(new Set(trimmedKeywords));
  return normalized.length ? normalized : undefined;
}

export function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

type PortableTextChild = {
  text?: unknown;
};

type PortableTextBlock = {
  children?: unknown;
};

/**
 * Convert Sanity Portable Text blocks into a single plain-text string.
 *
 * This is useful for schema payloads (e.g. FAQ JSON-LD) where rich text
 * markup is not supported and a clean text value is required.
 */
export function extractPortableText(blocks: unknown): string {
  if (!Array.isArray(blocks)) {
    return '';
  }

  return blocks
    .flatMap((block) => {
      if (!block || typeof block !== 'object') {
        return [];
      }

      // Portable Text stores visible copy in block.children[].text
      const children = (block as PortableTextBlock).children;
      if (!Array.isArray(children)) {
        return [];
      }

      return children
        .map((child) => {
          if (!child || typeof child !== 'object') {
            return '';
          }

          return (child as PortableTextChild).text;
        })
        .filter((text): text is string => typeof text === 'string' && text.trim().length > 0)
        .join(' ');
    })
    .filter((text) => text.length > 0)
    .join(' ')
    .trim();
}
